import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AddIcon from "@mui/icons-material/Add";
import CalendarViewWeekIcon from "@mui/icons-material/CalendarViewWeek";
import CalendarViewDayIcon from "@mui/icons-material/CalendarViewDay";
import Layout from "../components/layout/Layout";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import CalendarTable from "src/components/TournamentScheduler/CalendarTable";
import GameDetailsDialog from "src/components/TournamentScheduler/GameDetailsDialog";
import {
  getColorForTournament,
} from "src/components/TournamentScheduler/TournamentConstants";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ScheduledGame {
  scheduled_event_id: number;
  club_id: number;
  tournament_group_id: number;
  tournament_id: number;
  home_team_id: number;
  away_team_id: number;
  game_date: string;
  game_time: string;
  duration_minutes: number;
  field_location: string;
  status: string;
  created_at: string;
}

interface TournamentInfo {
  tournament_group_id: number;
  tournament_name: string;
  start_date: string;
  end_date: string;
  leagues: Array<{
    tournament_id: number;
    league_name: string;
    handicap: number;
  }>;
}

interface ApprovedTeam {
  team_id: number;
  team_name: string;
  total_handicap: number;
  players: Array<{
    player_id: number;
    first_name: string;
    last_name: string;
    handicap: number;
    position: number;
  }>;
}


/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function TournamentCalendar({
  devPort,
  clubId,
  managerMode,
  waiverNotifications,
}: {
  devPort: string;
  clubId: number;
  managerMode: number;
  waiverNotifications: boolean;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // md breakpoint is typically 900px

  const [allScheduledGames, setAllScheduledGames] = useState<ScheduledGame[]>([]);
  const [tournaments, setTournaments] = useState<TournamentInfo[]>([]);
  const [teamsMap, setTeamsMap] = useState<Record<number, ApprovedTeam>>({});
  const [weekStart, setWeekStart] = useState<Dayjs>(dayjs().startOf("week"));
  const [viewMode, setViewMode] = useState<"week" | "day">(isMobile ? "day" : "week");
  const [selectedDay, setSelectedDay] = useState<Dayjs>(dayjs());

  // Create-game dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTournamentGroup, setSelectedTournamentGroup] = useState<number>(0);

  const TIME_START_HOUR = 7;
  const TIME_END_HOUR = 18;

  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let h = TIME_START_HOUR; h <= TIME_END_HOUR; h += 1) {
      slots.push(`${h.toString().padStart(2, "0")}:00`);
      slots.push(`${h.toString().padStart(2, "0")}:30`);
    }
    return slots;
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 130,   // must hold 200ms before drag starts
        tolerance: 5, // allow up to 5px wiggle during the delay
      },
    })
  );

  // Fetch all tournaments for the club
  const loadTournaments = useCallback(() => {
    const startDate = weekStart.clone().subtract(7, "day").format("YYYY-MM-DD");
    const endDate = weekStart.clone().add(21, "day").format("YYYY-MM-DD");
    fetch(`${devPort}tournament_list/${clubId}/${startDate}/${endDate}`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          // Group by tournament_group_id and build TournamentInfo
          // SQL returns: tournament_id, tournament, league, start_date, end_date, handicap, team_maximum, team_count, signups_open, tournament_group_id
          const groups: Record<number, TournamentInfo> = {};
          data.forEach((row: any) => {
            const groupId = row[9]; // tournament_group_id (index 9)
            if (!groupId || groupId === undefined || groupId === null) {
              console.warn("Skipping row with undefined tournament_group_id:", row);
              return;
            }
            const numGroupId = Number(groupId);
            if (isNaN(numGroupId)) {
              console.warn("Skipping row with invalid tournament_group_id:", groupId);
              return;
            }
            if (!groups[numGroupId]) {
              groups[numGroupId] = {
                tournament_group_id: numGroupId,
                tournament_name: row[1] || "Unknown Tournament", // tournament name
                start_date: row[3] || "", // start_date
                end_date: row[4] || "", // end_date
                leagues: [],
              };
            }
            groups[numGroupId].leagues.push({
              tournament_id: row[0] || 0, // tournament_id
              league_name: row[2] || "Unknown League", // league
              handicap: row[5] || 0, // handicap
            });
          });
          setTournaments(Object.values(groups));

          // Load schedules for all tournament groups
          Object.keys(groups).forEach((groupIdStr) => {
            const groupId = Number(groupIdStr);
            if (isNaN(groupId)) {
              console.warn("Skipping invalid groupId:", groupIdStr);
              return;
            }
            fetch(`${devPort}tournament_schedule/${clubId}/${groupId}`)
              .then((r) => r.json())
              .then((games) => {
                if (Array.isArray(games)) {
                  setAllScheduledGames((prev) => {
                    const filtered = prev.filter(
                      (g) => g.tournament_group_id !== groupId
                    );
                    return [...filtered, ...games];
                  });
                }
              })
              .catch((err) => {
                console.error(`Error loading schedule for tournament_group ${groupId}:`, err);
              });
          });
        }
      })
      .catch(console.error);
  }, [devPort, clubId, weekStart]);

  // Load teams for all leagues
  const loadTeams = useCallback(() => {
    tournaments.forEach((tournament) => {
      tournament.leagues.forEach((league) => {
        fetch(`${devPort}tournament_approved_teams/${clubId}/${league.tournament_id}`)
          .then((r) => r.json())
          .then((data) => {
            if (Array.isArray(data)) {
              setTeamsMap((prev) => {
                const updated = { ...prev };
                data.forEach((team: ApprovedTeam) => {
                  updated[team.team_id] = team;
                });
                return updated;
              });
            }
          })
          .catch(console.error);
      });
    });
  }, [devPort, clubId, tournaments]);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  useEffect(() => {
    if (tournaments.length > 0) {
      loadTeams();
    }
  }, [tournaments, loadTeams]);

  // Week dates
  const weekDates = useMemo(() => {
    const dates: string[] = [];
    for (let i = 0; i < 7; i += 1) {
      dates.push(weekStart.clone().add(i, "day").format("YYYY-MM-DD"));
    }
    return dates;
  }, [weekStart]);

  // Dates to display based on view mode
  const displayDates = useMemo(() => {
    if (viewMode === "day") {
      return [selectedDay.format("YYYY-MM-DD")];
    }
    return weekDates;
  }, [viewMode, selectedDay, weekDates]);

  // League name map
  const leagueNameMap = useMemo(() => {
    const map: Record<number, { name: string; groupId: number }> = {};
    tournaments.forEach((t) => {
      t.leagues.forEach((l) => {
        map[l.tournament_id] = { name: l.league_name, groupId: t.tournament_group_id };
      });
    });
    return map;
  }, [tournaments]);

  // Tournament name map
  const tournamentNameMap = useMemo(() => {
    const map: Record<number, string> = {};
    tournaments.forEach((t) => {
      map[t.tournament_group_id] = t.tournament_name;
    });
    return map;
  }, [tournaments]);

  const teamName = (teamId: number) => teamsMap[teamId]?.team_name ?? `Team #${teamId}`;

  const getPlayerIdsOnTeam = (teamId: number): number[] => {
    const team = teamsMap[teamId];
    if (!team) return [];
    return team.players.map((p) => p.player_id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    if (!activeId.startsWith("game-") || !overId.startsWith("slot-")) return;

    const gameId = Number(activeId.replace("game-", ""));
    // overId format: slot-YYYY-MM-DD__HH:mm
    const payloadStr = overId.replace("slot-", "");
    const [targetDate, targetTime] = payloadStr.split("__");
    if (!targetDate || !targetTime) return;

    const game = allScheduledGames.find((g) => g.scheduled_event_id === gameId);
    if (!game) return;
    if (game.game_date === targetDate && game.game_time === targetTime) return;

    const payload = {
      scheduled_event_id: game.scheduled_event_id,
      club_id: game.club_id,
      tournament_group_id: game.tournament_group_id,
      tournament_id: game.tournament_id,
      home_team_id: game.home_team_id,
      away_team_id: game.away_team_id,
      game_date: targetDate,
      game_time: targetTime,
      duration_minutes: game.duration_minutes,
      field_location: game.field_location,
    };

    fetch(`${devPort}tournament_schedule_game_update`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then(() => {
        loadTournaments();
      })
      .catch(console.error);
  };

  const deleteGame = (gameId: number) => {
    if (!window.confirm("Are you sure you want to delete this scheduled game?")) return;
    fetch(`${devPort}tournament_schedule_game_delete`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scheduled_event_id: gameId }),
    })
      .then((r) => r.json())
      .then(() => loadTournaments())
      .catch(console.error);
  };

  // Create new game
  const openNewGameDialog = () => {
    if (tournaments.length === 0) {
      alert("No tournaments available. Please create a tournament first.");
      return;
    }
    const firstTournament = tournaments[0];
    setSelectedTournamentGroup(firstTournament.tournament_group_id);
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  // Game details dialog state
  const [selectedGame, setSelectedGame] = useState<ScheduledGame | null>(null);
  const [gameDetailsDialogOpen, setGameDetailsDialogOpen] = useState(false);

  const openGameDetails = (game: ScheduledGame) => {
    setSelectedGame(game);
    setGameDetailsDialogOpen(true);
  };

  const closeGameDetails = () => {
    setGameDetailsDialogOpen(false);
    setSelectedGame(null);
  };

  // Only show for managers
  if (managerMode !== -1) return null;

  return (
    <Layout
      managerMode={managerMode}
      waiverNotifications={waiverNotifications}
      devPort={devPort}
    >
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 2, mb: 0 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              //justifyContent="space-between"
              //alignItems={{ xs: "stretch", sm: "center" }}
              spacing={2}
            >
              <Typography variant="h4" fontWeight={600}>
                Tournament Calendar
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={openNewGameDialog}
                sx={{ mr: 1 }}
              >
                Schedule New Game
              </Button>

              {/* View mode toggle */}
              <ButtonGroup size="small" variant="outlined">
                <Button
                  variant={viewMode === "week" ? "contained" : "outlined"}
                  startIcon={<CalendarViewWeekIcon />}
                  onClick={() => setViewMode("week")}
                >
                  Week
                </Button>
                <Button
                  variant={viewMode === "day" ? "contained" : "outlined"}
                  startIcon={<CalendarViewDayIcon />}
                  onClick={() => {
                    setViewMode("day");
                    // Default to today if it falls within the current week, otherwise first day of week
                    const today = dayjs();
                    const inWeek = weekDates.includes(today.format("YYYY-MM-DD"));
                    setSelectedDay(inWeek ? today : weekStart);
                  }}
                >
                  Day
                </Button>
              </ButtonGroup>

              {/* Navigation */}
              <Grid container spacing={1} alignItems="center">
                <Grid size="auto">
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 40, height: 40 }}
                    onClick={() => {
                      if (viewMode === "week") {
                        setWeekStart((prev) => prev.clone().subtract(7, "day"));
                      } else {
                        setSelectedDay((prev) => prev.clone().subtract(1, "day"));
                      }
                    }}
                  >
                    <NavigateBeforeIcon />
                  </Button>
                </Grid>
                <Grid size="auto" sx={{ flex: 1, minWidth: 0 }}>
                  <DatePicker
                    value={viewMode === "week" ? weekStart : selectedDay}
                    onChange={(val: Dayjs | null) => {
                      if (!val) return;
                      if (viewMode === "week") {
                        setWeekStart(val.startOf("week"));
                      } else {
                        setSelectedDay(val);
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        sx: {
                          "& .MuiInputBase-root": {
                            height: 40,
                          },
                        },
                        InputProps: {
                          sx: { fontWeight: 600, fontSize: "0.95rem" },
                        },
                      },
                    }}
                    format={viewMode === "week" ? "MMM D, YYYY" : "ddd, MMM D YYYY"}
                  />
                </Grid>
                <Grid size="auto">
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 40, height: 40 }}
                    onClick={() => {
                      if (viewMode === "week") {
                        setWeekStart((prev) => prev.clone().add(7, "day"));
                      } else {
                        setSelectedDay((prev) => prev.clone().add(1, "day"));
                      }
                    }}
                  >
                    <NavigateNextIcon />
                  </Button>
                </Grid>
              </Grid>
              {/* Today button 
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  if (viewMode === "week") {
                    setWeekStart(dayjs().startOf("week"));
                  } else {
                    setSelectedDay(dayjs());
                  }
                }}
              >
                Today
              </Button>*/}
            </Stack>

            {/* Legend */}
            <Stack spacing={1}>
              {/* Tournament background colours */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Tournaments:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {tournaments.map((t) => {
                    const color = getColorForTournament(t.tournament_group_id);
                    return (
                      <Chip
                        key={t.tournament_group_id}
                        label={t.tournament_name}
                        sx={{
                          backgroundColor: color.bg,
                          color: color.text,
                          border: `1px solid ${color.border}`,
                        }}
                      />
                    );
                  })}
                </Stack>
              </Box>

              {/* League chip colours
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Leagues:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {tournaments.flatMap((t) =>
                    t.leagues.map((l) => {
                      const chipColor = getColorForLeagueChip(l.tournament_id);
                      return (
                        <Chip
                          key={l.tournament_id}
                          label={l.league_name}
                          size="small"
                          sx={{
                            backgroundColor: chipColor.bg,
                            color: chipColor.text,
                          }}
                        />
                      );
                    })
                  )}
                </Stack>
              </Box> */}
            </Stack>
          </Paper>
          {/* Calendar Table */}
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <CalendarTable
              weekDates={displayDates}
              timeSlots={timeSlots}
              allScheduledGames={allScheduledGames}
              teamName={teamName}
              leagueNameMap={leagueNameMap}
              tournamentNameMap={tournamentNameMap}
              teamsMap={teamsMap}
              getPlayerIdsOnTeam={getPlayerIdsOnTeam}
              openGameDetails={openGameDetails}
            />
          </DndContext>

          {/* Game Details / Edit Dialog */}
          {selectedGame && (
            <GameDetailsDialog
              open={gameDetailsDialogOpen}
              onClose={closeGameDetails}
              game={selectedGame}
              teamsMap={teamsMap}
              teamName={teamName}
              leagueNameMap={leagueNameMap}
              tournamentNameMap={tournamentNameMap}
              managerMode={managerMode}
              devPort={devPort}
              clubId={clubId}
              loadTournaments={loadTournaments}
              tournaments={tournaments}
              onDelete={deleteGame}
              allScheduledGames={allScheduledGames}
              getPlayerIdsOnTeam={getPlayerIdsOnTeam}
            />
          )}

          {/* Create Game Dialog (reuses GameDetailsDialog in create mode) */}
          <GameDetailsDialog
            open={createDialogOpen}
            onClose={closeCreateDialog}
            game={null}
            teamsMap={teamsMap}
            teamName={teamName}
            leagueNameMap={leagueNameMap}
            tournamentNameMap={tournamentNameMap}
            managerMode={managerMode}
            devPort={devPort}
            clubId={clubId}
            loadTournaments={loadTournaments}
            tournaments={tournaments}
            onDelete={deleteGame}
            initialGameDate={weekStart.format("YYYY-MM-DD")}
            initialTournamentGroupId={selectedTournamentGroup}
          />
        </Stack>
      </Box>
    </Layout>
  );
}


